import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { AppHeader } from "../components/AppHeader";
import { Button, Field } from "../components/UIKit";

const API = process.env.REACT_APP_BACKEND_URL;

// Generic UX message for every failure case (invalid, expired, already used,
// user missing, account switched to Google-only). Do NOT branch on server
// response — the server intentionally returns the same 400 for all.
const GENERIC_ERROR = "Ce lien de réinitialisation est invalide ou a expiré. Demandez un nouveau lien.";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);

  // No token in the URL → immediate generic error, do not accept input.
  useEffect(() => {
    if (!token) setError(GENERIC_ERROR);
  }, [token]);

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (loading || done || !token) return;
    setError(null);
    if (!password || password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
      // Strip the token from the URL so it doesn't linger in the history.
      setParams({}, { replace: true });
      // Redirect to /login shortly so the user immediately re-authenticates.
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError("Trop de tentatives. Réessayez dans quelques minutes.");
      } else {
        setError(GENERIC_ERROR);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--mf-bg)", display: "flex", flexDirection: "column" }}>
      <AppHeader background="surface" clickable={false} />
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 24px calc(32px + env(safe-area-inset-bottom))" }}>
        <div style={{ width: "100%", maxWidth: 440 }} className="mf-anim-in" data-testid="reset-password-page">
          <div style={{ marginBottom: 24, textAlign: "center" }}>
            <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: "var(--mf-text)", letterSpacing: -0.4 }}>
              {done ? "Mot de passe mis à jour" : "Nouveau mot de passe"}
            </h2>
            <p style={{ margin: "6px 0 0", fontSize: 15, color: "var(--mf-text-secondary)" }}>
              {done ? "Vous pouvez maintenant vous connecter." : "Choisissez un nouveau mot de passe pour votre compte."}
            </p>
          </div>

          {done ? (
            <div
              data-testid="reset-password-success"
              style={{
                padding: 20, borderRadius: 12, background: "var(--mf-bg-muted)",
                border: "1px solid var(--mf-border)", color: "var(--mf-text)",
                fontSize: 15, lineHeight: 1.5, marginBottom: 20, textAlign: "center",
              }}
            >
              Redirection vers la page de connexion…
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="reset-password-form">
              <Field
                testID="reset-password-new-input"
                label="Nouveau mot de passe"
                type={show ? "text" : "password"}
                value={password}
                onChange={setPassword}
                placeholder="8 caractères minimum"
                autoComplete="new-password"
              />
              <Field
                testID="reset-password-confirm-input"
                label="Confirmation"
                type={show ? "text" : "password"}
                value={confirm}
                onChange={setConfirm}
                placeholder="Retapez le mot de passe"
                autoComplete="new-password"
                error={error}
              />
              <div style={{ marginBottom: 16, fontSize: 13, color: "var(--mf-text-secondary)" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    data-testid="reset-password-show-toggle"
                    checked={show}
                    onChange={(e) => setShow(e.target.checked)}
                  />
                  Afficher le mot de passe
                </label>
              </div>
              <Button
                testID="reset-password-submit-button"
                type="submit"
                loading={loading}
                disabled={!token}
              >
                Enregistrer le nouveau mot de passe
              </Button>
            </form>
          )}

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <Link
              to="/login"
              data-testid="reset-password-goto-login"
              style={{ color: "var(--mf-text-secondary)", fontSize: 14, textDecoration: "none" }}
            >
              Retour à la <span style={{ color: "var(--mf-brand)", fontWeight: 700 }}>connexion</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
